import Cookies from 'js-cookie';
import { addDays, differenceInSeconds } from 'date-fns';

const STORAGE_PREFIX = 'hitryai_'; 
const TOKEN_BALANCE = `${STORAGE_PREFIX}balance`;
const LAST_CLAIM = `${STORAGE_PREFIX}lastClaim`;
const LAST_TOOL_CLICK = `${STORAGE_PREFIX}lastToolClick`;
const TOOL_CLICKS_TODAY = `${STORAGE_PREFIX}toolClicksToday`;
const CLAIM_STREAK = `${STORAGE_PREFIX}claimStreak`;
const TOOL_STREAK = `${STORAGE_PREFIX}toolStreak`;

export const getTokenBalance = (): number => {
  const balance = localStorage.getItem(TOKEN_BALANCE);
  return balance ? parseInt(balance, 10) : 0;
};

export const setTokenBalance = (amount: number): void => {
  localStorage.setItem(TOKEN_BALANCE, amount.toString());
};

export const getLastClaimTime = (): Date | null => {
  const lastClaim = localStorage.getItem(LAST_CLAIM);
  return lastClaim ? new Date(lastClaim) : null;
};

export const setLastClaimTime = (date: Date): void => {
  localStorage.setItem(LAST_CLAIM, date.toISOString());
};

export const canClaimTokens = (): boolean => {
  const lastClaim = getLastClaimTime();
  if (!lastClaim) return true;

  const now = new Date();
  const secondsSinceLastClaim = differenceInSeconds(now, lastClaim);
  return secondsSinceLastClaim >= 24 * 60 * 60;
};

export const getNextClaimTime = (): Date | null => {
  const lastClaim = getLastClaimTime();
  if (!lastClaim) return null;

  const nextClaimTime = new Date(lastClaim);
  nextClaimTime.setHours(nextClaimTime.getHours() + 24);
  return nextClaimTime;
};

export const getClaimStreak = (): number => {
  const streak = localStorage.getItem(CLAIM_STREAK);
  return streak ? parseInt(streak, 10) : 0;
};

export const setClaimStreak = (streak: number): void => {
  localStorage.setItem(CLAIM_STREAK, streak.toString());
};

export const getToolStreak = (): number => {
  const streak = localStorage.getItem(TOOL_STREAK);
  return streak ? parseInt(streak, 10) : 0;
};

export const setToolStreak = (streak: number): void => {
  localStorage.setItem(TOOL_STREAK, streak.toString());
};

export const claimDailyTokens = (): { newBalance: number; streak: number; bonus: number } => {
  const now = new Date();
  const lastClaim = getLastClaimTime();
  const currentStreak = getClaimStreak();
  
  let newStreak = currentStreak;
  let bonus = 0;
  
  if (lastClaim) {
    const daysSinceLastClaim = Math.floor(differenceInSeconds(now, lastClaim) / (24 * 60 * 60));
    if (daysSinceLastClaim === 1) {
      // Consecutive day
      newStreak = currentStreak + 1;
      // Add bonus every 7 days
      if (newStreak % 7 === 0) {
        bonus = 100;
      }
    } else if (daysSinceLastClaim > 1) {
      // Streak broken
      newStreak = 1;
    }
  } else {
    // First claim
    newStreak = 1;
  }
  
  const currentBalance = getTokenBalance();
  const newBalance = currentBalance + 50 + bonus;
  
  setTokenBalance(newBalance);
  setLastClaimTime(now);
  setClaimStreak(newStreak);
  
  return { newBalance, streak: newStreak, bonus };
};

// Tool click tracking
export const getLastToolClick = (): string | null => {
  return localStorage.getItem(LAST_TOOL_CLICK);
};

export const setLastToolClick = (date: string): void => {
  localStorage.setItem(LAST_TOOL_CLICK, date);
};

export const getToolClicksToday = (): number => {
  const clicks = localStorage.getItem(TOOL_CLICKS_TODAY);
  return clicks ? parseInt(clicks, 10) : 0;
};

export const setToolClicksToday = (clicks: number): void => {
  localStorage.setItem(TOOL_CLICKS_TODAY, clicks.toString());
};

export const handleToolClick = (): { streak: number } => {
  const today = new Date().toISOString().split('T')[0];
  const lastClick = getLastToolClick();
  const currentStreak = getToolStreak();
  
  let newStreak = currentStreak;
  
  if (lastClick) {
    const lastClickDate = new Date(lastClick);
    const daysSinceLastClick = Math.floor(differenceInSeconds(new Date(), lastClickDate) / (24 * 60 * 60));
    
    if (daysSinceLastClick === 1) {
      // Consecutive day
      newStreak = currentStreak + 1;
    } else if (daysSinceLastClick > 1) {
      // Streak broken
      newStreak = 1;
    }
  } else {
    // First click
    newStreak = 1;
  }
  
  setLastToolClick(today);
  setToolStreak(newStreak);
  const clicks = getToolClicksToday();
  setToolClicksToday(clicks + 1);
  
  return { streak: newStreak };
};